/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { upperCase } from "lodash";
import { allowUpdateEntity, allowDeleteEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { fileIcons } from "../../../shared/constants/icons";
import { UserInfo } from "../../../shared/types/user-info";
import { getContractAttachmentSearchFilterOptions } from "../company-attachments/company-attachment";
import { Contract, ContractAttachment } from "./contract.type";
import { ContractService } from "./contract.service";

@Injectable({
  providedIn: "root",
})
export class ContractAttachmentTableService {
  currentUser: UserInfo;

  constructor(
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private contractService: ContractService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  getContractAttachmentsTableConfig = (config?: DataTableConfig): DataTable<ContractAttachment> => {
    const { hidden, shown, actionColumn, inlineEdit } = config || {};

    const columns: DataTableColumn<ContractAttachment>[] = (
      [
        {
          prop: "extension",
          name: "File Type",
          icon: {
            class: this.getIconClass,
            tooltip: this.getTooltipValue,
          },
        },
        {
          prop: "name",
          name: "Name",
          link: {
            target: "_blank",
            path: (contractAttachment) => contractAttachment.url,
            useHref: true,
          },
        },
        {
          prop: "description",
          name: "Description",
          inlineEdit: {
            control: "input",
            disabled: (contractAttachment) =>
              inlineEdit?.disabled?.(contractAttachment) ||
              this.currentUser.hasRight(["viewer"]) ||
              this.currentUser.isCompanyOrViewAsCompany(),
          },
        },
      ] as DataTableColumn<ContractAttachment>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (contractAttachment) =>
            col?.inlineEdit?.disabled?.(contractAttachment) || !allowUpdateEntity(contractAttachment, this.currentUser),
        },
      }));

    return {
      id: "contractAttachment",
      entityName: "Attachment",
      syncWithQueryParams: true,
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: this.viewContractAttachment,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteContractAttachment,
              hidden: (contractAttachment) => !allowDeleteEntity(contractAttachment, this.currentUser),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getContractAttachmentSearchFilterOptions(this.currentUser, { hidden, shown }),
        },
      ],
    };
  };

  getContractAttachments = (event: GetDataEvent<ContractAttachment>, contract: Contract): void => {
    const { onError, onResult, queryParams } = event;
    this.contractService
      .getContractAttachments(contract.id, {
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  update = (event: UpdateDataEvent<ContractAttachment>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      const data = { description: updatedValues.description };
      this.contractService.updateContractAttachment(row.contractId, row.id, data).subscribe(
        (result): void => {
          onResult(result);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  // #region Private methods
  private viewContractAttachment = (contractAttachment: ContractAttachment): void => {
    const data = [
      { name: "Name", value: contractAttachment.name },
      { name: "Description", value: contractAttachment.description },
    ];
    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data: data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private deleteContractAttachment = (
    contractAttachment: ContractAttachment,
    table: DataTableComponent<ContractAttachment>,
  ): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "contractAttachment",
        data: contractAttachment,
        url: `${this.contractService.basePath}/${contractAttachment.contractId}/contract-attachments`,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private getTooltipValue = (contractAttachment: ContractAttachment): string => upperCase(contractAttachment.extension);

  private getIconClass = (contractAttachment: ContractAttachment): string =>
    `fa ${fileIcons[contractAttachment.extension] || fileIcons.default}`;

  // #endregion
}
